import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { WakzyLogo } from "./WakzyLogo";
import { Button } from "./ui/button";
import { Menu, X, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";

const NAV = [
  { to: "/inventory", label: "Inventory" },
  { to: "/estimate", label: "Estimate" },
  { to: "/financing", label: "Financing" },
  { to: "/trade-in", label: "Trade-In" },
  { to: "/service", label: "Service" },
  { to: "/auctions", label: "Auctions" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

export function PublicShell({ children, transparent = false }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--wakzy-bg)" }}>
      <header
        className={`${transparent || isHome ? "absolute" : "sticky"} top-0 left-0 right-0 z-30 ${
          transparent || isHome ? "" : "glass border-b border-white/5"
        }`}
        data-testid="public-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="logo-glow flex-shrink-0" data-testid="public-logo-link">
            <WakzyLogo className="h-10" asLink={false} />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#D4AF37]" : "text-slate-300 hover:text-white"
                  }`
                }
                data-testid={`public-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/install")}
              className="text-[#D4AF37] hover:text-white text-xs"
              data-testid="public-install-btn"
            >
              Get the App
            </Button>
            {user && user.role && user.role !== "customer" && (
              <Button onClick={() => navigate("/admin")} className="btn-gold rounded-sm h-9 text-xs" data-testid="public-admin-btn">
                Staff Dashboard <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
          <button
            className="lg:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden glass border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 py-4 grid grid-cols-2 gap-2">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm ${isActive ? "text-[#D4AF37]" : "text-slate-300"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <PublicFooter />
    </div>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-white/5 mt-20 py-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-4 gap-10">
        <div>
          <WakzyLogo className="h-10" />
          <p className="text-sm text-slate-300 mt-4 leading-relaxed">
            <strong className="font-bold text-white">Wakzy Auto Sales &amp; Services LLC</strong> — luxury inventory, paperless purchase, and white-glove service under one roof.
          </p>
        </div>
        <div>
          <div className="label-eyebrow mb-3" style={{ color: "var(--wakzy-gold)" }}>Shop</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li><Link to="/inventory" className="hover:text-white">Inventory</Link></li>
            <li><Link to="/estimate" className="hover:text-white">Quick Estimate</Link></li>
            <li><Link to="/auctions" className="hover:text-white">Auctions</Link></li>
            <li><Link to="/trade-in" className="hover:text-white">Trade-In</Link></li>
            <li><Link to="/financing" className="hover:text-white">Financing</Link></li>
          </ul>
        </div>
        <div>
          <div className="label-eyebrow mb-3" style={{ color: "var(--wakzy-gold)" }}>Wakzy Network</div>
          <ul className="space-y-2 text-sm text-slate-300">
            <li>
              <a href="https://www.wakzyauto.com" target="_blank" rel="noopener noreferrer" className="hover:text-white inline-flex items-center gap-1" data-testid="footer-link-wakzyauto">
                wakzyauto.com <span className="text-[10px] text-slate-500">↗</span>
              </a>
              <div className="text-[11px] text-slate-500 mt-0.5">Main dealership site</div>
            </li>
            <li>
              <a href="https://www.wakzyautotech.com" target="_blank" rel="noopener noreferrer" className="hover:text-white inline-flex items-center gap-1" data-testid="footer-link-wakzyautotech">
                wakzyautotech.com <span className="text-[10px] text-slate-500">↗</span>
              </a>
              <div className="text-[11px] text-slate-500 mt-0.5">Service & tech center</div>
            </li>
            <li><Link to="/service" className="hover:text-white">Book Service</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
            <li><Link to="/reviews" className="hover:text-white">Customer Reviews</Link></li>
            <li><Link to="/install" className="hover:text-white">Download the App</Link></li>
          </ul>
        </div>
        <div>
          <div className="label-eyebrow mb-3" style={{ color: "var(--wakzy-gold)" }}>Visit Us</div>
          <p className="text-sm text-slate-300 leading-relaxed">
            <strong className="font-bold text-white">Wakzy Auto Sales &amp; Services LLC</strong><br />
            121 Rainbow Way<br />
            Fayetteville, GA 30214<br />
            <a href="tel:+17706472563" className="hover:text-white">(770) 647-2563</a><br />
            <a href="mailto:info@wakzyauto.com" className="text-[#D4AF37] hover:underline">info@wakzyauto.com</a>
          </p>
          <p className="text-xs text-slate-500 mt-4">
            Mon–Sat · 9:00am – 8:00pm<br />
            Sunday · 11:00am – 5:00pm
          </p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <div>© {new Date().getFullYear()} <strong className="font-bold text-white">Wakzy Auto Sales &amp; Services LLC</strong>. All rights reserved.</div>
        <div className="flex items-center gap-4">
          <span>Secure · Encrypted · Built for the showroom floor.</span>
          <Link to="/login" className="text-slate-500 hover:text-[#D4AF37] transition-colors" data-testid="footer-staff-login">Staff Portal</Link>
        </div>
      </div>
    </footer>
  );
}
